/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * How a user's asset group scope reads in the admin user list (MEGA-776), and which
 * users an asset group filter matches.
 */

import type { AdminAssetGroup, AdminUserSummary } from './types'

type User = Pick<AdminUserSummary, 'assetGroups' | 'companyId'>

/**
 * "All" when the user sees every group of their company — scoped to the whole company,
 * or listing every current group of it — "All customers" for the staff wildcard, else
 * the group names.
 */
export const assetGroupsLabel = (user: User, groups: readonly AdminAssetGroup[]): string => {
  if (user.assetGroups.includes('*')) return 'All customers'
  const ids = user.assetGroups.flatMap((entry) => (entry === '*' ? [] : [entry.id]))
  if (ids.length === 0) return 'All'
  const ofCompany = groups.filter((group) => group.customerId === user.companyId)
  if (ofCompany.length > 0 && ofCompany.every((group) => ids.includes(group.companyId))) return 'All'
  return user.assetGroups.flatMap((entry) => (entry === '*' ? [] : [entry.name ?? `#${String(entry.id)}`])).join(', ')
}

/** In the group: it is listed, or the user sees the whole company the group belongs to. */
export const inAssetGroup = (user: User, group: Pick<AdminAssetGroup, 'companyId' | 'customerId'>): boolean => {
  if (user.assetGroups.includes('*')) return false
  if (user.assetGroups.length === 0) return user.companyId === group.customerId
  return user.assetGroups.some((entry) => entry !== '*' && entry.id === group.companyId)
}
